import DOMPurify from 'dompurify'
import { createParser } from 'eventsource-parser'
import ChatBot, {
  ChatBotProvider,
  Message as MessageInterface,
  Styles,
  useChatHistory,
  useChatWindow,
  useFirstInteraction,
  useMessages,
  useTextArea
} from 'neuraum-angus'
import React, {
  CSSProperties,
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react'
import showdown from 'showdown'

import { get } from '@js/common/api'
import { getUrl } from '@js/common/getUrlByDomain'
import { getTestParam } from '@js/common/utils'

import {
  brandedStyles,
  defaultStyles
} from '@f-fertighaus/applications/ChatAssistant/components/styles'
import {
  FLOW_ID_STORAGE_KEY,
  useChatWebtracking
} from '@f-fertighaus/applications/ChatAssistant/components/tracking'
import { errorLogger } from '@f-fertighaus/utils/errorLogger'

import { BubbleIcon, RobotIcon, SendIcon } from './components/components'
import { MessageWithLinkPreview } from './components/LinkPreview'

import './ChatAssistant.css'

const themes = [
  { id: 'minimal_midnight', version: '0.1.0' },
  { id: 'simple_blue', version: '0.1.0' }
]

let hasError = false

const MAX_SESSION_DURATION_MS = 1000 * 60 * 60 * 2
const getStorageKey = (vendorNid: string, vendorSlug: string) =>
  `chat-history-${vendorNid}-${vendorSlug}`

const showdownConverter = new showdown.Converter({
  simpleLineBreaks: true
})

interface StreamResponses {
  (args: {
    params
    currentState: Message[]
    vendorNid: string
    houseNid?: string
    botBubbleStyle: CSSProperties
  }): Promise<string>
}

const streamResponses: StreamResponses = async ({
  params,
  currentState,
  vendorNid,
  houseNid,
  botBubbleStyle
}) => {
  const CSRF_TOKEN = window['🍪'].read('csrftoken')

  try {
    const getTenMostRecentEntries = (history: Message[]) =>
      history.slice(
        history.length > 10 ? history.length - 10 : 0,
        history.length
      )

    const responseStream = await fetch(`/-/chat-assistant`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': CSRF_TOKEN
      },
      body: JSON.stringify(
        houseNid
          ? {
              message: params.userInput,
              context: getTenMostRecentEntries(currentState),
              vendor_nid: vendorNid,
              house_nid: houseNid,
              stream: true
            }
          : {
              message: params.userInput,
              context: getTenMostRecentEntries(currentState),
              vendor_nid: vendorNid,
              stream: true
            }
      )
    })

    const reader = responseStream.body?.getReader()
    if (!reader) throw new Error('Failed to get response stream reader')

    const decoder = new TextDecoder()
    let accumulatedResponse = ''

    const parser = createParser({
      onEvent: event => {
        const chunk = event.data
        if (chunk === '[DONE]') {
          return
        }

        accumulatedResponse = chunk

        const intermediateHtml = showdownConverter.makeHtml(accumulatedResponse)
        const cleanHtml = DOMPurify.sanitize(intermediateHtml)

        params.streamMessage(
          <div
            style={{ ...botBubbleStyle, display: 'inline' }}
            className='rcb-bot-message rcb-bot-message-entry'>
            <MessageWithLinkPreview message={cleanHtml} />
          </div>
        )
      }
    })

    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        parser.feed(decoder.decode(value, { stream: true }))
      }
    } finally {
      await params.endStreamMessage()
      reader.releaseLock()
    }

    return accumulatedResponse
  } catch (error) {
    await params.injectMessage(
      'Der Hausbau-Assistent hat ein vorübergehendes Problem. Er wird bald wieder verfügbar sein!'
    )
    hasError = true
    return ''
  }
}
type MessageAuthor = 'assistant' | 'user'

interface Message {
  type: MessageAuthor
  message: string
}

interface MessageAction {
  type: string
  payload: string
}

function messagesReducer(state: Message[], action: MessageAction) {
  switch (action.type) {
    case 'ADD_ASSISTANT_MESSAGE':
      return [
        ...state,
        {
          type: 'assistant',
          message: action.payload
        }
      ]
    case 'ADD_USER_MESSAGE':
      return [
        ...state,
        {
          type: 'user',
          message: action.payload
        }
      ]
    case 'GET_MESSAGES':
      return state
    default:
      return state
  }
}

type StyleType = 'branded' | 'default'

interface ChatAssistantProps {
  vendorNid: string
  vendorName: string
  vendorSlug: string
  style: StyleType | Styles
  houseNid?: string
  wtLabel: string
}

export const chatStyles: Record<StyleType, Styles> = {
  branded: brandedStyles,
  default: defaultStyles
}

const getWelcomeMessage = (vendorName: string, chatConsent: boolean) => {
  const welcomeMessageAfterConsent = `Herzlich Willkommen bei ${vendorName}! Ich bin Ihr KI-Hausbau-Assistent.</br></br>Ich helfe Ihnen gerne bei allen Fragen rund um den Bau mit ${vendorName}, deren Hausportfolio und der Verwirklichung Ihres Traumhauses.</br></br>Egal, ob Sie mehr über Hausentwürfe, die Vorteile des Bauens mit ${vendorName} oder den Bauprozess erfahren möchten – ich bin für Sie da.</br></br>Wie kann ich Ihnen weiterhelfen?`

  const welcomeMessageBeforeConsent = `Herzlich Willkommen bei ${vendorName}! Ich bin Ihr KI-Hausbau-Assistent und freue mich, Ihnen bei allen Fragen rund um den Bau Ihres Traumhauses zu helfen. <br/><br/>Dieser Chat beantwortet allgemeine Fragen zu unseren Hausmodellen, dem Bauprozess und den Vorteilen des Bauens mit ${vendorName}. <br/><br/>Persönliche Daten wie Namen oder Adressen sind nicht erforderlich – wir empfehlen Ihnen, diese nicht einzugeben. <br/><br/>Ihre Eingaben werden verarbeitet, um Ihnen passende Antworten zu geben. Mehr dazu erfahren Sie in unserer <a target="_blank" href=${getUrl(
    'privacyPolicy'
  )()}>Datenschutzerklärung</a>.<br/><br/>Mit Klick auf „Chat starten“ akzeptieren Sie diese Bedingungen und starten den Chat.`

  return chatConsent ? welcomeMessageAfterConsent : welcomeMessageBeforeConsent
}

const cleanupExpiredChatHistory = (storageKey: string) => {
  const entries = JSON.parse(localStorage.getItem(storageKey) || '[]')
  const now = new Date().getTime()
  const lastEntry = entries[entries.length - 1]
  const lastEntryTimestamp = new Date(lastEntry?.timestamp).getTime()
  const isExpiredSession =
    entries.length > 0 && now - lastEntryTimestamp > MAX_SESSION_DURATION_MS

  if (isExpiredSession) {
    localStorage.setItem(storageKey, '[]')
    localStorage.setItem(FLOW_ID_STORAGE_KEY, '')
  }
}

export const ChatAssistant = (props: ChatAssistantProps) => {
  const storageKey = getStorageKey(props.vendorNid, props.vendorSlug)
  const isBVersion = getTestParam('NR_11284_chat_CTA_KI_copy_update') === 'B'
  const [chatConsent, setChatConsent] = useState(
    document.cookie.includes('chatConsent=true')
  )

  const { getHistoryMessages } = useChatHistory()

  const initialMessage: Message = {
    type: 'assistant' as MessageAuthor,
    message: showdownConverter.makeHtml(
      getWelcomeMessage(props.vendorName, chatConsent)
    )
  }
  const [state, dispatch] = useReducer<Message[], MessageAction>(
    messagesReducer,
    [initialMessage],
    initialMessages => {
      cleanupExpiredChatHistory(storageKey)
      const localStorageMessages = JSON.parse(
        localStorage.getItem(storageKey) || '[]'
      )
      const isOngoingChatSession = localStorageMessages.length > 0

      if (isOngoingChatSession) {
        const normalizedMessages = localStorageMessages.map(
          (message: MessageInterface) => ({
            type: message.sender.toUpperCase() === 'BOT' ? 'assistant' : 'user',
            message: message.content
          })
        )
        return normalizedMessages
      }

      return initialMessages
    }
  )
  const [sendMessage, setSendMessage] = React.useState(false)

  const addBotMessage = (message: string) => {
    dispatch({ type: 'ADD_ASSISTANT_MESSAGE', payload: message })
  }

  const addUserMessage = (message: string) => {
    dispatch({ type: 'ADD_USER_MESSAGE', payload: message })
  }

  useEffect(() => {
    window._wt({
      category: 'chat-assistant',
      action: 'display',
      label: props.wtLabel,
      values: [{ type: 'version', value: 'engagement' }]
    })

    const handleUserSubmitText = () => {
      setSendMessage(true)
    }

    window.addEventListener('rcb-user-submit-text', handleUserSubmitText)
    return () => {
      window.removeEventListener('rcb-user-submit-text', handleUserSubmitText)
    }
  }, [])

  const flow = {
    start: {
      chatDisabled: () => {
        return chatConsent ? false : true
      },
      message: () => {
        const historyMessages = getHistoryMessages()
        const isOngoingSessions = historyMessages.length > 0
        if (isOngoingSessions) {
          /* Chatbot needs to return a message in "start" block. It is designed that way */
          const lastMessageContent =
            historyMessages[historyMessages.length - 1].content
          return lastMessageContent
        }
        return initialMessage.message
      },
      options: () =>
        chatConsent ? [] : { items: ['Chat starten'], sendOutput: false },
      path: () => (chatConsent ? 'loop' : 'consent_given'),
      function: params => {
        if (params.userInput === 'Chat starten') {
          const date = new Date()
          date.setFullYear(date.getFullYear() + 1)
          document.cookie = `chatConsent=true; expires=${date.toUTCString()}; path=/`
          setChatConsent(true)
          window._wt({
            category: `CTA-button`,
            action: 'click',
            label: 'chat_app_terms_acceptance',
            values: [{ type: 'text', value: 'Chat starten' }]
          })
          return
        }
        addUserMessage(params.userInput)
      }
    },
    consent_given: {
      message: `Vielen Dank! Wie kann ich Ihnen helfen?<br/><br/>Möchten Sie mehr über unsere Hausmodelle, den Bauprozess oder die Vorteile des Bauens mit ${props.vendorName} erfahren?<br /><br />Ich bin bereit, Ihre Fragen zu beantworten!`,
      path: 'loop',
      function: params => {
        addUserMessage(params.userInput)
      }
    },
    loop: {
      function: async params => {
        addUserMessage(params.userInput)
      },
      message: async params => {
        const responseMessage = await streamResponses({
          params,
          currentState: state,
          vendorNid: props.vendorNid,
          houseNid: props.houseNid,
          botBubbleStyle:
            typeof props.style === 'string'
              ? chatStyles[props.style].botBubbleStyle
              : props.style.botBubbleStyle
        })

        addBotMessage(responseMessage)
      },
      path: () => {
        if (hasError) {
          return 'start'
        }
        return 'loop'
      }
    }
  }

  useChatWebtracking(props.vendorName)

  const chatIcon = isBVersion ? BubbleIcon : RobotIcon

  return (
    <ChatBotProvider>
      <>
        <ChatBot
          settings={{
            tooltip: { mode: 'NEVER' },
            event: {
              rcbUserSubmitText: true,
              rcbToggleChatWindow: true,
              rcbPostInjectMessage: true
            },
            chatHistory: {
              storageType: 'LOCAL_STORAGE',
              storageKey: storageKey,
              autoLoad: true
            },
            chatButton: { icon: chatIcon },
            header: {
              title: (
                <div className='Chatbot-title'>
                  <p>{`${props.vendorName} Hausbau-Assistent`}</p>
                  <p>Ihr KI-Assistent für den Hausbau</p>
                </div>
              ),
              avatar: `/nr-assets/icon-library-v6.svg/#chat-message-16px`,
              showAvatar: false
            },
            botBubble: {
              dangerouslySetInnerHtml: true
            },
            chatInput: {
              enabledPlaceholderText: 'Wie Kann ich Ihnen weiterhelfen?',
              sendButtonIcon: SendIcon
            },
            general: {
              primaryColor: 'var(--branding-main-color)'
            }
          }}
          flow={flow}
          themes={themes}
          styles={
            typeof props.style === 'string'
              ? chatStyles[props.style]
              : props.style
          }
        />
        <UpdateSettingsAfterFirstInteraction />
        <FocusTextAreaAndAutoExpand
          sendMessageStatus={sendMessage}
          setSendMessageStatus={setSendMessage}
        />
      </>
    </ChatBotProvider>
  )
}

interface FocusTextAreaAndAutoExpandProps {
  sendMessageStatus: boolean
  setSendMessageStatus: React.Dispatch<React.SetStateAction<boolean>>
}

const FocusTextAreaAndAutoExpand = (props: FocusTextAreaAndAutoExpandProps) => {
  const { focusTextArea } = useTextArea()
  const { isChatWindowOpen } = useChatWindow()
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const { sendMessageStatus, setSendMessageStatus } = props
  const isFirstOpen = useRef(true)

  const inputQuery = 'textarea.rcb-chat-input-textarea'

  useEffect(() => {
    function handleInputChange() {
      this.style.height = 'auto'
      this.style.height = this.scrollHeight + 'px'
    }

    if (isChatWindowOpen) {
      const textareaElement = document.querySelector(
        inputQuery
      ) as HTMLTextAreaElement
      if (textareaElement) {
        inputRef.current = textareaElement
        inputRef.current.addEventListener('input', handleInputChange)
      }
      setTimeout(() => {
        focusTextArea()
      }, 50)
    }

    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('input', handleInputChange)
      }
    }
  }, [isChatWindowOpen, focusTextArea])

  useEffect(() => {
    if (inputRef.current && sendMessageStatus) {
      inputRef.current.style.height = '25px'
      setSendMessageStatus(false)
    }
  }, [sendMessageStatus, setSendMessageStatus])

  useEffect(() => {
    function warmupChatService() {
      if (isChatWindowOpen && isFirstOpen.current) {
        isFirstOpen.current = false

        const EMPTY_PAYLOAD = null
        const AUTH_TOKEN =
          'DY7kFsQ6Js4i03tIB8LhQ2jg3etsb9EktxxS8WFGeqxOF5ODKW6Yr27Qf94Vz0La'
        get(
          'https://rag-service-vendor-dot-neuraum-data-production.appspot.com/warmup',
          EMPTY_PAYLOAD,
          {
            Authorization: `Bearer ${AUTH_TOKEN}`
          }
        ).catch(errorLogger)
      }
    }

    warmupChatService()
    const main = document.querySelector('main#content')

    if (isChatWindowOpen) {
      main?.classList.remove('isolate')
    } else {
      main?.classList.add('isolate')
    }
  }, [isChatWindowOpen])

  return null
}

const UpdateSettingsAfterFirstInteraction = () => {
  const { hasInteractedPage } = useFirstInteraction()
  const { getHistoryMessages, setHistoryMessages } = useChatHistory()
  const { messages, removeMessage } = useMessages()
  const [firstMessageRemoved, setFirstMessageRemoved] = useState(false)

  useEffect(() => {
    const cleanupInitialHistoryMessage = () => {
      /* If there is a message in history (localStorage) then Chatbot will treat it as message his message because  flow `start` has to return some message. To offset that behaviour first message will be removed */
      const historyMessages = getHistoryMessages()
      const isOngoingSession = historyMessages.length > 0
      if (isOngoingSession) {
        const lastMessage = messages[messages.length - 1]
        removeMessage(lastMessage.id)
        setFirstMessageRemoved(true)
      }
      const hasChatConsent = document.cookie.includes('chatConsent=true')

      if (!hasChatConsent) {
        setHistoryMessages([])
      }
    }
    if (hasInteractedPage && firstMessageRemoved === false) {
      cleanupInitialHistoryMessage()
    }
  }, [
    firstMessageRemoved,
    getHistoryMessages,
    hasInteractedPage,
    messages,
    removeMessage,
    setHistoryMessages
  ])

  return null
}
